export type SpecialFieldType = 'tel' | 'email' | 'name' | 'birthDate';
export type StandardFieldType =
  | 'text'
  | 'textarea'
  | 'radio'
  | 'checkbox'
  | 'number'
  | 'date';
export type FieldType = SpecialFieldType | StandardFieldType;

export type UID = string;

export type Option = {
  uid: UID;
  text: string;
};

export type FieldCondition = {
  public: {
    visibled: boolean;
    required: boolean;
  };
  internal: {
    required: boolean;
  };
};

export type Field = {
  uid: UID;
  type: FieldType;
  name: string;
  description?: string;
  defaultValues?: string[];
  condition?: FieldCondition;
};

export const createDefaultFieldCondition = (): FieldCondition => {
  return {
    public: {
      visibled: true,
      required: true,
    },
    internal: {
      required: false,
    },
  };
};

export const defaultFieldCondition: FieldCondition =
  createDefaultFieldCondition();

export const publicFields = (fields: AnyField[]) => {
  return fields.filter((f) => {
    return f.condition == undefined || f.condition.public.visibled;
  });
};

export type TextField = Field & {
  type: 'text';
};

export type TextareaField = Field & {
  type: 'textarea';
};

export type RadioField = Field & {
  type: 'radio';
  options: Option[];
  isShowPulldown?: boolean;
};

export type CheckboxField = Field & {
  type: 'checkbox';
  options: Option[];
};

export type TelField = Field & {
  type: 'tel';
};

export type EmailField = Field & {
  type: 'email';
};

export type NameField = Field & {
  type: 'name';
};

export type NumberField = Field & {
  type: 'number';
};

export type DateField = Field & {
  type: 'date';
};

export type BirthDateField = Field & {
  type: 'birthDate';
  enableDefaultJapaneseInput?: boolean;
};

export type AnyField =
  | TextField
  | TextareaField
  | RadioField
  | CheckboxField
  | TelField
  | EmailField
  | NameField
  | NumberField
  | DateField
  | BirthDateField;

export type FormSetting = {
  fields: AnyField[];
  deletedFields?: AnyField[];
};

export type OptionValue = {
  uid: UID;
};

export const isOptionValue = (value: any): value is OptionValue => {
  return typeof value.uid === 'string';
};

export type FieldResponseValue = string | OptionValue;

export type FieldResponse = {
  uid: UID;
  values: FieldResponseValue[];
};

export type FormResponse = {
  fields: FieldResponse[];
};

export const isSpecialFieldType = (type: FieldType) =>
  type == 'email' || type == 'tel' || type == 'name' || type == 'birthDate';

export const getSpecialFieldValue = (
  formSetting: FormSetting,
  formResponse: FormResponse,
  fieldType: SpecialFieldType
): string | undefined => {
  const field = formSetting.fields.find((f) => f.type == fieldType);
  if (!field) {
    return undefined;
  }
  const fieldResponse = formResponse.fields.find((f) => f.uid == field.uid);
  const fieldValue = fieldResponse?.values?.[0];
  if (!fieldValue) {
    return undefined;
  }
  if (typeof fieldValue === 'string') {
    return fieldValue;
  } else if (field.type === 'checkbox' || field.type === 'radio') {
    return field.options.find((o) => o.uid === fieldValue.uid)?.text;
  } else {
    return undefined;
  }
};

export const createDefaultFieldResponses = (
  formSetting: FormSetting | undefined
): FieldResponse[] => {
  try {
    if (!formSetting?.fields) {
      return [];
    }
    const defaultFieldValues = formSetting.fields
      .filter((f) => {
        return f.defaultValues && f.defaultValues.length > 0;
      })
      .map((f) => {
        try {
          // 選択肢型（radio/checkbox）の場合は、デフォルト値が実際の選択肢に存在するかチェック
          if ((f.type === 'radio' || f.type === 'checkbox') && f.options) {
            const validDefaultValues = (f.defaultValues || []).filter(
              (defaultValue) => {
                // 選択肢のuidまたはtextと一致するかチェック
                return f.options.some(
                  (option) =>
                    option.uid === defaultValue || option.text === defaultValue
                );
              }
            );

            // radioの場合、有効な値が1つもなければ空配列を返す
            // checkboxの場合、有効な値のみを返す
            if (f.type === 'radio' && validDefaultValues.length === 0) {
              return {
                uid: f.uid,
                values: [],
              } as FieldResponse;
            }

            return {
              uid: f.uid,
              values: validDefaultValues,
            } as FieldResponse;
          }

          // 選択肢型以外はそのままデフォルト値を使用
          return {
            uid: f.uid,
            values: [...(f.defaultValues || [])],
          } as FieldResponse;
        } catch (error) {
          // 個別のフィールド処理でエラーが発生した場合は、そのフィールドを空配列で返す
          console.error(
            `Error processing default values for field ${f.uid}:`,
            error
          );
          return {
            uid: f.uid,
            values: [],
          } as FieldResponse;
        }
      });
    return defaultFieldValues;
  } catch (error) {
    // 全体的なエラーが発生した場合は空配列を返す
    console.error('Error in createDefaultFieldResponses:', error);
    return [];
  }
};
